import { EVButton, EVGrid, EVTypography } from 'lib/components';
import React, { useEffect, useMemo } from 'lib/react';
import { useTranslation } from 'lib/translation';
import { useDispatch, useSelector } from 'lib/redux';
import history from 'store/CreateHistory';
import {
  NO_SELECTEDPRODUCTS_VALIDATION, COMPLEX_MARKED_VALIDATION, NO_SELECTEDPIN_VALIDATION, NO_BLUEPRINTID_VALIDATION, MULTIFAMILY,
  PRODUCT_IDS,
} from 'constants/AppConstants';
import { ENABLE_GEOMNI_ESX_ADDON_LOGIC } from 'constants/FeatureFlag.constants';
import { FINALIZE_ORDER_ROUTE, ORDER_BLUEPRINT_ROUTE } from 'constants/RouteConstants';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  isEntitledForHidePricingSelector,
  featureFlagSelector,
  isSubscriptionDashboardV2EnabledSelector,
} from 'utils/appSelectors.utils';
import {
  addProductPriceToEstimate,
  areAllMapPointsReadyForPlaceOrder,
  getEstimatedTotalText,
} from '../Order.utils';
import { filterNotSetProducts } from './Product/ProductList.util';
import OrderStyles from '../OrderStyles';
import {
  openValidationMessageDialog,
  setSubscriptionEstimatedTotal,
  updateSelectedProduct,
} from '../Order.actions';
import { REGULAR_DELIVERY_PRODUCT_ID, getSubscriptionEstimatedTotal } from '../Order.constants';

function EstimatedTotal({ className, ...rest }) {
  const classes = OrderStyles();
  const { t } = useTranslation();
  const {
    currencyTypeId,
    selectedPrimaryProduct,
    selectedPrimaryDeliveryProduct,
    selectedWallsProduct,
    selectedWallsDeliveryProduct,
    selectedOtherProducts,
    primarySubscriptionProductsWithUsage,
    userProducts,
    propertyType,
    isComplex,
    mapPoints,
    isBillingAffiliateUser,
    bluePrintId,
  } = useSelector(({
    order, orderAddresses, placeOrder, userData: { initParameters, userProducts: products },
  }) => ({
    ...order,
    mapPoints: orderAddresses.mapPoints,
    currencyTypeId: initParameters?.currencyTypeId,
    userProducts: products,
    isBillingAffiliateUser: initParameters?.isBillingAffiliate,
    bluePrintFileId: placeOrder.bluePrintFileId,
    bluePrintId: placeOrder.bluePrintId,
  }));
  const isEntitledForHidePricing = useSelector(isEntitledForHidePricingSelector);
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);
  const isESXEnabled = useSelector(featureFlagSelector(ENABLE_GEOMNI_ESX_ADDON_LOGIC));
  const dispatch = useDispatch();
  const isBluePrintOrder = history.location.pathname === ORDER_BLUEPRINT_ROUTE;
  const numberOfPins = isBluePrintOrder ? 1 : mapPoints.length;
  // Blueprint orders don't use map pins, so the address-readiness guard doesn't apply there.
  const isNextDisabled = !isBluePrintOrder && !areAllMapPointsReadyForPlaceOrder(mapPoints);

  const selectedSubscriptionProduct = useMemo(() => {
    if (!isSubscriptionDashboardV2Enabled || !selectedPrimaryProduct?.ProductID) return null;
    return (primarySubscriptionProductsWithUsage ?? []).find(
      (p) => Number(p.productId) === Number(selectedPrimaryProduct.ProductID) && p.isSubscription,
    ) || null;
  }, [
    isSubscriptionDashboardV2Enabled,
    primarySubscriptionProductsWithUsage,
    selectedPrimaryProduct,
  ]);

  const estimatedTotal = useMemo(() => {
    if (selectedSubscriptionProduct) {
      return getSubscriptionEstimatedTotal({
        subscriptionProduct: selectedSubscriptionProduct,
        selectedPrimaryDeliveryProduct,
        selectedOtherProducts,
        orderedUnits: Math.max(numberOfPins, 1),
      });
    }

    // Include products from both propertyType and NotSet (where data products like Property Data are stored)
    // #genai: use optional chaining (Sonar S6582); userProducts.NotSet is either an object or null/undefined, so semantics are unchanged
    const notSetProducts = (propertyType !== MULTIFAMILY && userProducts.NotSet?.PrimaryProducts)
      ? userProducts.NotSet.PrimaryProducts.filter((p) => filterNotSetProducts(p, propertyType, isESXEnabled))
      : [];
    const productList = userProducts[propertyType] ? [
      ...userProducts[propertyType].PrimaryProducts,
      ...notSetProducts,
    ] : [];
    const bundleDiscountList = productList
      .filter((product) => selectedOtherProducts[product.ProductID])
      .map((product) => product.BundleDiscounts).flat();

    if (selectedPrimaryProduct) bundleDiscountList.push(...selectedPrimaryProduct.BundleDiscounts);
    if (selectedWallsProduct) bundleDiscountList.push(...selectedWallsProduct.BundleDiscounts);

    let [totalMin, totalMax] = productList.reduce(
      (totals, product) => {
        const [estimateMin, estimateMax] = totals;

        if (selectedOtherProducts[product.ProductID]) {
          return addProductPriceToEstimate(
            estimateMin,
            estimateMax,
            product,
            selectedOtherProducts[product.ProductID],
            bundleDiscountList,
            selectedPrimaryProduct,
            isESXEnabled,
          );
        }
        return totals;
      },
      [0, 0],
    );

    if (selectedPrimaryProduct) {
      [totalMin, totalMax] = addProductPriceToEstimate(
        totalMin,
        totalMax,
        selectedPrimaryProduct,
        selectedPrimaryDeliveryProduct,
        bundleDiscountList,
        selectedPrimaryProduct,
        isESXEnabled,
      );
    }
    if (selectedWallsProduct) {
      [totalMin, totalMax] = addProductPriceToEstimate(
        totalMin,
        totalMax,
        selectedWallsProduct,
        selectedWallsDeliveryProduct,
        bundleDiscountList,
        selectedPrimaryProduct,
        isESXEnabled,
      );
    }

    return [Number(totalMin.toFixed(2)), Number(totalMax.toFixed(2))];
  }, [
    selectedPrimaryProduct,
    selectedPrimaryDeliveryProduct,
    selectedWallsProduct,
    selectedWallsDeliveryProduct,
    selectedOtherProducts,
    selectedSubscriptionProduct,
    numberOfPins,
    isESXEnabled,
  ]);

  const subscriptionEstimatedTotalText = useMemo(() => {
    if (!selectedSubscriptionProduct) return null;
    return getEstimatedTotalText(estimatedTotal, currencyTypeId, 1);
  }, [selectedSubscriptionProduct, estimatedTotal, currencyTypeId]);

  useEffect(() => {
    dispatch(setSubscriptionEstimatedTotal(subscriptionEstimatedTotalText));
  }, [dispatch, subscriptionEstimatedTotalText]);

  const getValidationMessage = () => {
    if (!selectedPrimaryProduct && !selectedWallsProduct && Object.keys(selectedOtherProducts).length === 0) {
      return NO_SELECTEDPRODUCTS_VALIDATION;
    }
    if (isBluePrintOrder) {
      if (!bluePrintId) return NO_BLUEPRINTID_VALIDATION;
    } else {
      if (mapPoints.length === 0) return NO_SELECTEDPIN_VALIDATION;
      if (isComplex && mapPoints.length < 2) return COMPLEX_MARKED_VALIDATION;
    }
    return null;
  };

  const handleNextClick = () => {
    const message = getValidationMessage();
    if (message) dispatch(openValidationMessageDialog(message));
    else {
      // Non-subscription roof + ESX: API expects regular primary delivery.
      // Subscription V2 keeps the selected delivery upgrade for summary and pricing.
      if (
        isESXEnabled
        && Object.hasOwn(selectedOtherProducts, PRODUCT_IDS.ESX)
        && !selectedSubscriptionProduct
      ) {
        dispatch(updateSelectedProduct({
          selectedPrimaryDeliveryProduct: REGULAR_DELIVERY_PRODUCT_ID,
          selectedOtherProducts: {
            ...selectedOtherProducts,
            [PRODUCT_IDS.ESX]: REGULAR_DELIVERY_PRODUCT_ID,
          },
        }));
      }
      if (history.unblock) {
        history.unblock();
        delete history.unblock;
      }
      history.push(FINALIZE_ORDER_ROUTE);
    }
  };

  return (
    <EVGrid container className={clsx(classes.estimationContainer, className)} {...rest}>
      {!isBillingAffiliateUser && !isEntitledForHidePricing && (
      <EVTypography>
        {`${t('EstimatedTotal')}: `}
        {getEstimatedTotalText(estimatedTotal, currencyTypeId, selectedSubscriptionProduct ? 1 : numberOfPins)}
      </EVTypography>
      )}
      <EVButton
        id="next-btn"
        variant="contained"
        color="primary"
        onClick={handleNextClick}
        disabled={isNextDisabled}
      >
        {t('common_next')}
      </EVButton>
    </EVGrid>
  );
}

EstimatedTotal.propTypes = {
  className: PropTypes.string,
};

EstimatedTotal.defaultProps = {
  className: '',
};

export default EstimatedTotal;
